(function () {
  'use strict';

  var STATE_KEY = 'lg_state';
  var STATE_VERSION = 1;
  var SWIPE_THRESHOLD = 40;
  var DEFAULT_SLIDES_MAX = 3;
  var DEFAULT_PER_SLIDE = 8;
  var DEFAULT_DISPLAY_COUNT = 24;
  var DEFAULT_EXPLORATION = [0.18, 0.33, 0.45];
  var DEFAULT_ROOM_CAP = 5;
  var PRODUCT_NAME_MIN_GAP = 3; // same productName must be > 3 positions apart
  var EXPLORATION_WEIGHT = 0.3; // seen-image weight multiplier
  var FATIGUE_VISITS = 5;
  var FATIGUE_SEEN_RATIO = 0.8;

  // -------- consent & storage --------
  function canPersist() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      var allowed = cp && typeof cp.analyticsProcessingAllowed === 'function'
        ? cp.analyticsProcessingAllowed()
        : false;
      if (!allowed) return false;
      var t = '__lg_test__';
      window.localStorage.setItem(t, '1');
      window.localStorage.removeItem(t);
      return true;
    } catch (e) {
      return false;
    }
  }

  function readState() {
    if (!canPersist()) return null;
    try {
      var raw = window.localStorage.getItem(STATE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== STATE_VERSION) return null;
      if (!Array.isArray(parsed.seen)) parsed.seen = [];
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    if (!canPersist()) return;
    try {
      window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) {
      /* quota / private mode — silent */
    }
  }

  function genUUID() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    // Fallback
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // -------- seeded PRNG --------
  function hashCode(str) {
    var h = 0;
    if (!str) return 0;
    for (var i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    // force unsigned for seeding
    return h >>> 0;
  }

  function mulberry32(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // -------- weighted selection without replacement --------
  function weightedSelect(items, weights, rng, count) {
    var pool = items.slice();
    var w = weights.slice();
    var picked = [];
    var n = Math.min(count, pool.length);
    for (var k = 0; k < n; k++) {
      var total = 0;
      for (var i = 0; i < w.length; i++) total += w[i];
      if (total <= 0) {
        // fallback: uniform
        var idx = Math.floor(rng() * pool.length);
        picked.push(pool[idx]);
        pool.splice(idx, 1);
        w.splice(idx, 1);
        continue;
      }
      var r = rng() * total;
      var chosen = pool.length - 1;
      for (var j = 0; j < w.length; j++) {
        r -= w[j];
        if (r <= 0) { chosen = j; break; }
      }
      picked.push(pool[chosen]);
      pool.splice(chosen, 1);
      w.splice(chosen, 1);
    }
    return picked;
  }

  // -------- eligibility & classification helpers --------
  function isExploration(img) {
    return (img.confidence === 'low') || (typeof img.daysLive === 'number' && img.daysLive < 7);
  }
  function isScored(img) {
    return !isExploration(img);
  }
  function isWinner(img) {
    return img.tier === 'winner' && (img.confidence === 'high' || img.confidence === 'medium');
  }

  function baseWeight(img) {
    var s = typeof img.score === 'number' ? img.score : 0;
    // ensure strictly positive so low-scored items can still be picked
    return Math.max(s, 0.05);
  }

  function applySeenPenalty(img, seenSet) {
    var w = baseWeight(img);
    if (seenSet[img.filename]) w *= EXPLORATION_WEIGHT;
    return w;
  }

  // -------- selection algorithm --------
  function scoresAreFlat(pool) {
    if (!pool || pool.length <= 1) return true;
    var first = pool[0].score;
    for (var i = 1; i < pool.length; i++) {
      if (pool[i].score !== first) return false;
    }
    return true;
  }

  function regroupIntoSlides(flat, perSlide) {
    var slides = [];
    for (var g = 0; g < flat.length; g += perSlide) {
      slides.push(flat.slice(g, g + perSlide));
    }
    return slides;
  }

  function selectImages(pool, config, state, rng) {
    var perSlide = (config && config.perSlide) || DEFAULT_PER_SLIDE;
    var displayCount = (config && typeof config.displayCount === 'number') ? config.displayCount : DEFAULT_DISPLAY_COUNT;
    var slidesMax = (config && config.slidesMax) || Math.max(1, Math.ceil(displayCount / perSlide));
    var explorationRates = (config && Array.isArray(config.explorationRates)) ? config.explorationRates : DEFAULT_EXPLORATION;
    var roomCap = (config && config.roomTypeCap) || DEFAULT_ROOM_CAP;

    var seenSet = {};
    if (state && state.seen) {
      for (var i = 0; i < state.seen.length; i++) seenSet[state.seen[i]] = true;
    }

    // Flat-scores short-circuit: if every image has the same score, skip the
    // deterministic head entirely and shuffle the full sequence using the seeded rng.
    // Diversity passes (room cap + productName spacing) still run on the result.
    if (scoresAreFlat(pool)) {
      var capFlat = Math.min(displayCount, pool.length);
      var weightsFlat = pool.map(function (img) { return applySeenPenalty(img, seenSet); });
      var pickedFlat = weightedSelect(pool, weightsFlat, rng, capFlat);
      var usedFlat = {};
      pickedFlat.forEach(function (it) { usedFlat[it.filename] = true; });
      var flatOut = pickedFlat;
      flatOut = enforceRoomCap(flatOut, pool, usedFlat, roomCap);
      flatOut = enforceProductSpacing(flatOut, PRODUCT_NAME_MIN_GAP);
      if (flatOut.length > displayCount) flatOut = flatOut.slice(0, displayCount);
      return regroupIntoSlides(flatOut, perSlide);
    }

    var scored = [];
    var exploration = [];
    for (var p = 0; p < pool.length; p++) {
      (isExploration(pool[p]) ? exploration : scored).push(pool[p]);
    }

    // Winner pool for deterministic slots
    var winners = pool.filter(isWinner);
    if (winners.length === 0) {
      // Fallback: pure recency sort
      var recency = pool.slice().sort(function (a, b) {
        return (a.daysLive || 0) - (b.daysLive || 0);
      });
      return finalizeSelection(recency, slidesMax, perSlide, roomCap, displayCount);
    }

    // Deterministic slide 1 positions 1-2: top-scored winners,
    // prefer unseen when visitor is returning
    var unseenWinners = winners.filter(function (w) { return !seenSet[w.filename]; });
    var deterministicSource = (state && unseenWinners.length >= 2) ? unseenWinners : winners;
    deterministicSource = deterministicSource.slice().sort(function (a, b) {
      return (b.score || 0) - (a.score || 0);
    });
    var det = deterministicSource.slice(0, 2);
    // if we couldn't get 2 unseen, fill with best remaining winners
    if (det.length < 2) {
      var fallbackWinners = winners.slice().sort(function (a, b) {
        return (b.score || 0) - (a.score || 0);
      });
      for (var fi = 0; fi < fallbackWinners.length && det.length < 2; fi++) {
        if (det.indexOf(fallbackWinners[fi]) === -1) det.push(fallbackWinners[fi]);
      }
    }

    var used = {};
    det.forEach(function (d) { used[d.filename] = true; });

    var slides = [];
    // Build each slide independently
    for (var s = 0; s < slidesMax; s++) {
      var explorationRate = explorationRates[s] != null ? explorationRates[s] : 0.33;
      var slotsNeeded = perSlide;
      var slotItems = [];
      var skusThisSlide = {};

      if (s === 0) {
        // Add deterministic seeds
        for (var di = 0; di < det.length; di++) {
          slotItems.push(det[di]);
          if (det[di].sku) skusThisSlide[det[di].sku] = true;
        }
        slotsNeeded = perSlide - slotItems.length;
      }

      var explorationSlots = Math.round(slotsNeeded * explorationRate);
      var scoredSlots = slotsNeeded - explorationSlots;

      // Filter each bucket to exclude already-used items + SKU dupes this slide
      function available(list) {
        return list.filter(function (img) {
          if (used[img.filename]) return false;
          if (img.sku && skusThisSlide[img.sku]) return false;
          return true;
        });
      }

      // Pick scored
      var scoredAvail = available(scored);
      var scoredWeights = scoredAvail.map(function (img) { return applySeenPenalty(img, seenSet); });
      var scoredPicks = weightedSelect(scoredAvail, scoredWeights, rng, scoredSlots);
      scoredPicks.forEach(function (img) {
        slotItems.push(img);
        used[img.filename] = true;
        if (img.sku) skusThisSlide[img.sku] = true;
      });

      // Pick exploration
      var explorationAvail = available(exploration);
      var explorationWeights = explorationAvail.map(function (img) { return applySeenPenalty(img, seenSet); });
      var explorationPicks = weightedSelect(explorationAvail, explorationWeights, rng, explorationSlots);
      explorationPicks.forEach(function (img) {
        slotItems.push(img);
        used[img.filename] = true;
        if (img.sku) skusThisSlide[img.sku] = true;
      });

      // Backfill if either bucket was short (e.g. exploration pool empty)
      if (slotItems.length < perSlide) {
        var combined = available(pool);
        var combinedWeights = combined.map(function (img) { return applySeenPenalty(img, seenSet); });
        var backfill = weightedSelect(combined, combinedWeights, rng, perSlide - slotItems.length);
        backfill.forEach(function (img) {
          slotItems.push(img);
          used[img.filename] = true;
          if (img.sku) skusThisSlide[img.sku] = true;
        });
      }

      if (slotItems.length === 0) break;
      slides.push(slotItems);
    }

    // Flatten then apply room-type cap swap, then productName spacing pass
    var flat = [];
    slides.forEach(function (slide) { flat = flat.concat(slide); });
    flat = enforceRoomCap(flat, pool, used, roomCap);
    flat = enforceProductSpacing(flat, PRODUCT_NAME_MIN_GAP);

    // Cap to exact displayCount (last slide may be partial if not evenly divisible)
    if (flat.length > displayCount) flat = flat.slice(0, displayCount);

    // Re-split into slides of perSlide
    var regrouped = [];
    for (var g = 0; g < flat.length; g += perSlide) {
      regrouped.push(flat.slice(g, g + perSlide));
    }
    return regrouped;
  }

  function enforceRoomCap(flat, fullPool, used, roomCap) {
    var counts = {};
    flat.forEach(function (img) {
      var r = img.roomType || '__none__';
      counts[r] = (counts[r] || 0) + 1;
    });

    // Build a pool of unused candidates grouped by room
    var poolByRoom = {};
    for (var i = 0; i < fullPool.length; i++) {
      var cand = fullPool[i];
      if (used[cand.filename]) continue;
      var rr = cand.roomType || '__none__';
      if (!poolByRoom[rr]) poolByRoom[rr] = [];
      poolByRoom[rr].push(cand);
    }
    // Sort each bucket by score desc
    Object.keys(poolByRoom).forEach(function (rr) {
      poolByRoom[rr].sort(function (a, b) { return (b.score || 0) - (a.score || 0); });
    });

    // Walk flat; whenever a room type is over cap, try swapping with best unused underrepresented
    for (var idx = 0; idx < flat.length; idx++) {
      var img = flat[idx];
      var r = img.roomType || '__none__';
      if (counts[r] <= roomCap) continue;

      // find an underrepresented room with a candidate
      var replacement = null;
      var rooms = Object.keys(poolByRoom);
      for (var k = 0; k < rooms.length; k++) {
        var rk = rooms[k];
        var currentCount = counts[rk] || 0;
        if (currentCount >= roomCap) continue;
        if (poolByRoom[rk].length === 0) continue;
        replacement = poolByRoom[rk].shift();
        counts[rk] = currentCount + 1;
        break;
      }
      if (replacement) {
        used[img.filename] = false;
        used[replacement.filename] = true;
        counts[r] -= 1;
        flat[idx] = replacement;
      }
    }
    return flat;
  }

  // -------- productName spacing pass --------
  // For each position i that collides with the previous `minGap` positions,
  // find the nearest j > i whose productName wouldn't collide at i, and swap.
  // Single forward pass — doesn't guarantee zero violations but handles the common case.
  function enforceProductSpacing(flat, minGap) {
    function collidesAt(pos, name) {
      if (!name) return false;
      for (var k = 1; k <= minGap && (pos - k) >= 0; k++) {
        if (flat[pos - k] && flat[pos - k].productName === name) return true;
      }
      return false;
    }

    for (var i = 1; i < flat.length; i++) {
      var name = flat[i] && flat[i].productName;
      if (!name) continue;
      if (!collidesAt(i, name)) continue;

      // find the nearest j > i whose productName wouldn't collide at position i
      for (var j = i + 1; j < flat.length; j++) {
        var cand = flat[j];
        if (!cand || !cand.productName) continue;
        if (cand.productName === name) continue; // swapping same name gains nothing
        if (collidesAt(i, cand.productName)) continue;
        var tmp = flat[i];
        flat[i] = flat[j];
        flat[j] = tmp;
        break;
      }
    }
    return flat;
  }

  function finalizeSelection(items, slidesMax, perSlide, roomCap, displayCount) {
    // used in fallback path
    var used = {};
    items.forEach(function (it) { used[it.filename] = true; });
    var cap = (typeof displayCount === 'number') ? displayCount : (slidesMax * perSlide);
    var flat = items.slice(0, cap);
    flat = enforceRoomCap(flat, items, used, roomCap);
    flat = enforceProductSpacing(flat, PRODUCT_NAME_MIN_GAP);
    if (flat.length > cap) flat = flat.slice(0, cap);
    var slides = [];
    for (var g = 0; g < flat.length; g += perSlide) {
      slides.push(flat.slice(g, g + perSlide));
    }
    return slides;
  }

  // -------- payload parsing (tolerant of Liquid metafield encoding variants) --------
  function parsePoolPayload(rawText) {
    if (!rawText) return null;
    var text = rawText.trim();
    if (!text) return null;
    var parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      return null;
    }
    // Case A: double-encoded — Liquid `| json` on a metafield drop returned a JSON-encoded string.
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch (e) { return null; }
    }
    if (!parsed || typeof parsed !== 'object') return null;
    // Case B: metafield wrapper shape {type, value}
    if (!parsed.pool && parsed.value != null) {
      if (typeof parsed.value === 'string') {
        try { parsed = JSON.parse(parsed.value); } catch (e) { return null; }
      } else if (typeof parsed.value === 'object') {
        parsed = parsed.value;
      }
    }
    // Backfill filename from url if missing (N8N pool builder omits it)
    if (parsed && Array.isArray(parsed.pool)) {
      for (var i = 0; i < parsed.pool.length; i++) {
        var entry = parsed.pool[i];
        if (entry && !entry.filename && entry.url) {
          var parts = entry.url.split('/');
          var last = parts[parts.length - 1];
          entry.filename = last.split('?')[0];
        }
      }
    }
    return parsed;
  }

  // -------- DOM rendering --------
  function escapeAttr(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function buildProductHref(productUrl) {
    if (!productUrl) return '#';
    return productUrl;
  }

  function renderTiles(container, slides, abGroup) {
    container.setAttribute('data-gallery-variant', abGroup);
    var html = '';
    var globalPos = 0;
    for (var s = 0; s < slides.length; s++) {
      html += '<div class="lg-page" role="group" aria-roledescription="slide" aria-label="Page ' + (s + 1) + ' of ' + slides.length + '">';
      var slide = slides[s];
      for (var i = 0; i < slide.length; i++) {
        globalPos += 1;
        var img = slide[i];
        var href = buildProductHref(img.productUrl);
        var src = img.url + (img.url && img.url.indexOf('?') === -1 ? '?width=600' : '');
        html += '<a href="' + escapeAttr(href) + '"'
          + ' class="lg-item gallery-tile"'
          + ' data-filename="' + escapeAttr(img.filename) + '"'
          + ' data-image-type="' + escapeAttr(img.imgType) + '"'
          + ' data-gallery-shot-type="' + escapeAttr(img.imgType) + '"'
          + ' data-gallery-room-type="' + escapeAttr(img.roomType) + '"'
          + ' data-sku="' + escapeAttr(img.sku) + '"'
          + ' data-url="' + escapeAttr(img.url) + '"'
          + ' data-performance-tier="' + escapeAttr(img.tier) + '"'
          + ' data-score="' + escapeAttr(img.score) + '"'
          + ' data-product-name="' + escapeAttr(img.productName) + '"'
          + ' data-gallery-week-code="' + escapeAttr(img.weekCode) + '"'
          + ' data-gallery-variant="' + escapeAttr(abGroup) + '"'
          + ' data-gallery-position="' + globalPos + '"'
          + ' aria-label="' + escapeAttr(img.productName) + '">'
          + '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(img.productName) + '" loading="lazy" decoding="async" width="600" height="600" />'
          + (abGroup === 'clean' ? '' : (
              '<div class="lg-overlay" style="background:none;padding:0;">'
              + '<p class="lg-name" title="' + escapeAttr(img.productName) + '"'
              + ' style="background:none;padding:0;border-radius:0;-webkit-backdrop-filter:none;backdrop-filter:none;box-shadow:none;transition:none;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.6);">'
              + escapeAttr(img.productName)
              + '</p>'
              + '</div>'
            ))
          + '</a>';
      }
      html += '</div>';
    }
    container.innerHTML = html;
    container.setAttribute('data-gallery-ready', '1');
  }

  function renderDots(root, slidesCount) {
    var dotsEl = root.querySelector('[data-lg-dots]');
    if (!dotsEl) return;
    if (slidesCount <= 1) {
      dotsEl.hidden = true;
      dotsEl.innerHTML = '';
      return;
    }
    dotsEl.hidden = false;
    var html = '';
    for (var i = 0; i < slidesCount; i++) {
      html += '<button type="button" class="lg-dot' + (i === 0 ? ' is-active' : '') + '"'
        + ' data-lg-dot="' + i + '"'
        + ' role="tab"'
        + ' aria-label="Go to page ' + (i + 1) + '"'
        + ' aria-selected="' + (i === 0 ? 'true' : 'false') + '"></button>';
    }
    dotsEl.innerHTML = html;
  }

  function showArrows(root, slidesCount) {
    var prev = root.querySelector('[data-lg-prev]');
    var next = root.querySelector('[data-lg-next]');
    var visible = slidesCount > 1;
    if (prev) prev.hidden = !visible;
    if (next) next.hidden = !visible;
  }

  // -------- slider behavior (reconstructed on each init) --------
  function attachSlider(root) {
    var track = root.querySelector('[data-lg-track]');
    if (!track) return null;
    var pages = Array.prototype.slice.call(track.children);
    var totalPages = pages.length;
    if (totalPages === 0) return null;

    var prevBtn = root.querySelector('[data-lg-prev]');
    var nextBtn = root.querySelector('[data-lg-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-lg-dot]'));
    var autoplayMs = parseInt(root.dataset.autoplay || '10000', 10);

    var index = 0;
    var timer = null;
    var hovered = false;
    var swiping = false;

    function render() {
      track.style.transform = 'translateX(' + (-index * 100) + '%)';
      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      pages.forEach(function (page, i) {
        page.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
    }

    function goTo(i) {
      if (totalPages <= 1) return;
      index = ((i % totalPages) + totalPages) % totalPages;
      render();
    }
    function next() { goTo(index + 1); }
    function prev() { goTo(index - 1); }

    function startTimer() {
      stopTimer();
      if (totalPages <= 1 || hovered || swiping || autoplayMs <= 0) return;
      timer = setInterval(next, autoplayMs);
    }
    function stopTimer() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function restart() { stopTimer(); startTimer(); }

    var handlers = [];
    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      handlers.push({ target: target, type: type, fn: fn, opts: opts });
    }

    if (prevBtn) on(prevBtn, 'click', function () { prev(); restart(); });
    if (nextBtn) on(nextBtn, 'click', function () { next(); restart(); });
    dots.forEach(function (dot) {
      on(dot, 'click', function () {
        var i = parseInt(dot.getAttribute('data-lg-dot') || '0', 10);
        goTo(i);
        restart();
      });
    });

    on(root, 'mouseenter', function () { hovered = true; stopTimer(); });
    on(root, 'mouseleave', function () { hovered = false; startTimer(); });
    on(root, 'focusin', function () { hovered = true; stopTimer(); });
    on(root, 'focusout', function () {
      if (!root.contains(document.activeElement)) { hovered = false; startTimer(); }
    });

    var startX = null;
    var deltaX = 0;
    on(track, 'touchstart', function (e) {
      if (!e.touches || e.touches.length === 0) return;
      swiping = true;
      stopTimer();
      startX = e.touches[0].clientX;
      deltaX = 0;
    }, { passive: true });
    on(track, 'touchmove', function (e) {
      if (startX == null || !e.touches || e.touches.length === 0) return;
      deltaX = e.touches[0].clientX - startX;
    }, { passive: true });
    on(track, 'touchend', function () {
      if (startX == null) { swiping = false; return; }
      if (deltaX > SWIPE_THRESHOLD) prev();
      else if (deltaX < -SWIPE_THRESHOLD) next();
      startX = null;
      deltaX = 0;
      swiping = false;
      restart();
    });
    on(track, 'touchcancel', function () {
      startX = null;
      deltaX = 0;
      swiping = false;
      restart();
    });

    function onVisibility() {
      if (document.hidden) stopTimer();
      else startTimer();
    }
    on(document, 'visibilitychange', onVisibility);

    render();
    startTimer();

    return {
      destroy: function () {
        stopTimer();
        handlers.forEach(function (h) {
          try { h.target.removeEventListener(h.type, h.fn, h.opts); } catch (e) {}
        });
        handlers.length = 0;
      }
    };
  }

  // -------- init / destroy (idempotent) --------
  var instances = new WeakMap();

  function destroyGallery(sectionEl) {
    var existing = instances.get(sectionEl);
    if (existing) {
      if (existing.slider && existing.slider.destroy) existing.slider.destroy();
      instances.delete(sectionEl);
    }
    delete sectionEl.dataset.lgInit;
  }

  function initGallery(sectionEl) {
    if (!sectionEl) return;
    destroyGallery(sectionEl);
    sectionEl.dataset.lgInit = '1';

    var sectionId = sectionEl.getAttribute('data-section-id') || '';
    var scriptEl = document.getElementById('gallery-pool-' + sectionId) || sectionEl.querySelector('[data-gallery-pool]');
    var container = document.getElementById('gallery-container-' + sectionId) || sectionEl.querySelector('[data-gallery-container]');
    if (!scriptEl || !container) {
      console.warn('[lifestyle-gallery] missing script or container', { sectionId: sectionId, scriptEl: !!scriptEl, container: !!container });
      return;
    }

    var rawText = scriptEl.textContent || '';
    var payload = parsePoolPayload(rawText);
    if (!payload) {
      console.warn('[lifestyle-gallery] failed to parse pool payload', rawText.slice(0, 120));
      return;
    }
    var pool = (payload && Array.isArray(payload.pool)) ? payload.pool : [];
    var config = (payload && payload.config) || {};

    if (pool.length === 0) {
      console.warn('[lifestyle-gallery] pool is empty after parse. payload keys:', payload ? Object.keys(payload) : null);
      return;
    }

    var perSlideAttr = parseInt(sectionEl.getAttribute('data-per-slide') || DEFAULT_PER_SLIDE, 10);
    config.perSlide = config.perSlide || perSlideAttr;
    // displayCount from metafield wins; else fall back to 24
    config.displayCount = (typeof config.displayCount === 'number') ? config.displayCount : DEFAULT_DISPLAY_COUNT;
    // Derive slide count from displayCount / perSlide (overrides any stored slidesMax)
    config.slidesMax = Math.max(1, Math.ceil(config.displayCount / config.perSlide));

    // -------- Visitor state --------
    var state = readState();
    var isNew = !state;
    if (isNew) {
      state = {
        v: STATE_VERSION,
        visitorId: genUUID(),
        seen: [],
        visits: 1,
        lastVisit: new Date().toISOString().slice(0, 10),
        abGroup: Math.random() < 0.5 ? 'clean' : 'overlay'
      };
    } else {
      var today = new Date().toISOString().slice(0, 10);
      if (state.lastVisit !== today) {
        state.visits = (state.visits || 0) + 1;
        state.lastVisit = today;
      }
      if (!state.abGroup) state.abGroup = Math.random() < 0.5 ? 'clean' : 'overlay';
      // Fatigue reset
      var seenRatio = pool.length > 0 ? (state.seen.length / pool.length) : 0;
      if (state.visits >= FATIGUE_VISITS || seenRatio >= FATIGUE_SEEN_RATIO) {
        state.seen = [];
        state.visits = 1;
      }
    }

    // Deterministic seeded PRNG: visitorId XOR 4-hour time block.
    // Different visitors always differ; same visitor reshuffles every 4h.
    var timeBlock = Math.floor(Date.now() / (4 * 60 * 60 * 1000));
    var visitorHash = hashCode(state.visitorId);
    var seed = (visitorHash ^ timeBlock) >>> 0;
    var rng = mulberry32(seed);

    // Pool < perSlide: show what's available, hide pagination
    if (pool.length < config.perSlide) {
      // single slide, whatever order is already in pool (stable)
      var single = pool.slice(0, pool.length);
      renderTiles(container, [single], state.abGroup);
      renderDots(sectionEl, 1);
      showArrows(sectionEl, 1);
      var slider0 = attachSlider(sectionEl);
      instances.set(sectionEl, { slider: slider0 });
      persistSeen(state, single);
      notifyReady(sectionEl);
      return;
    }

    var slides = selectImages(pool, config, isNew ? null : state, rng);
    if (!slides || slides.length === 0) return;

    renderTiles(container, slides, state.abGroup);
    renderDots(sectionEl, slides.length);
    showArrows(sectionEl, slides.length);

    // Persist seen images (dedup + cap history at 200 to avoid quota)
    var allPicked = [];
    slides.forEach(function (sl) { allPicked = allPicked.concat(sl); });
    persistSeen(state, allPicked);

    var slider = attachSlider(sectionEl);
    instances.set(sectionEl, { slider: slider });
    notifyReady(sectionEl);
  }

  function persistSeen(state, picked) {
    var seenMap = {};
    state.seen.forEach(function (f) { seenMap[f] = true; });
    picked.forEach(function (img) { if (img && img.filename) seenMap[img.filename] = true; });
    var merged = Object.keys(seenMap);
    // keep history bounded
    if (merged.length > 200) merged = merged.slice(merged.length - 200);
    state.seen = merged;
    writeState(state);
  }

  function notifyReady(sectionEl) {
    try {
      document.dispatchEvent(new CustomEvent('lifestyle-gallery:ready', {
        detail: { section: sectionEl }
      }));
    } catch (e) {}
  }

  function initAll(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-lg-slider]');
    Array.prototype.forEach.call(nodes, function (el) { initGallery(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target) return;
    var nodes = e.target.querySelectorAll('[data-lg-slider]');
    Array.prototype.forEach.call(nodes, function (el) { destroyGallery(el); });
  });

  // Expose for debugging/testing
  window.__lifestyleGallery = {
    init: initGallery,
    destroy: destroyGallery,
    selectImages: selectImages,
    mulberry32: mulberry32,
    hashCode: hashCode
  };
})();
